import { NodeProgram, NodeCircleProgram, createNodeCompoundProgram } from 'sigma/rendering';
import type { ProgramInfo } from 'sigma/rendering';
import { floatColor } from 'sigma/utils';
import type { NodeDisplayData, RenderParams } from 'sigma/types';

// Halo margin beyond the node body: proportional for small nodes, clamped for large
// ones so high-metric nodes read as bright cores with a tight rim instead of fog balls
const HALO_FACTOR = 1.0;
const HALO_MIN = 2.5;
const HALO_MAX = 5;
// Peak halo opacity at the node's edge
const GLOW_INTENSITY = 0.14;

const VERTEX_SHADER_SOURCE = /* glsl */ `
attribute vec4 a_id;
attribute vec4 a_color;
attribute vec2 a_position;
attribute float a_size;
attribute float a_coreSize;

uniform float u_sizeRatio;
uniform float u_pixelRatio;
uniform mat3 u_matrix;

varying vec4 v_color;
varying float v_coreRatio;

const float bias = 255.0 / 254.0;

void main() {
  gl_Position = vec4((u_matrix * vec3(a_position, 1)).xy, 0, 1);
  gl_PointSize = a_size / u_sizeRatio * u_pixelRatio * 2.0;

  v_coreRatio = a_coreSize / a_size;

  #ifdef PICKING_MODE
  v_color = a_id;
  #else
  v_color = a_color;
  #endif

  v_color.a *= bias;
}
`;

// Sigma blends with (ONE, ONE_MINUS_SRC_ALPHA), i.e. premultiplied alpha:
// the fragment color must be multiplied by its alpha or the halo renders at
// full RGB strength and washes out the background.
const FRAGMENT_SHADER_SOURCE = /* glsl */ `
precision mediump float;

varying vec4 v_color;
varying float v_coreRatio;

void main(void) {
  #ifdef PICKING_MODE
  // The halo must not enlarge the node's clickable area
  gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
  #else
  float r = length(gl_PointCoord - vec2(0.5, 0.5)) * 2.0;
  float t = clamp((r - v_coreRatio) / (1.0 - v_coreRatio), 0.0, 1.0);
  float falloff = (1.0 - t) * (1.0 - t);
  float a = falloff * ${GLOW_INTENSITY.toFixed(2)} * v_color.a;
  gl_FragColor = vec4(v_color.rgb * a, a);
  #endif
}
`;

const UNIFORMS = ['u_sizeRatio', 'u_pixelRatio', 'u_matrix'] as const;

class NodeGlowProgram extends NodeProgram<(typeof UNIFORMS)[number]> {
  getDefinition() {
    // resolve the WebGL enums here rather than at module load, so importing this
    // file doesn't touch the browser-only WebGLRenderingContext global (SSR/jsdom)
    const { UNSIGNED_BYTE, FLOAT, POINTS } = WebGLRenderingContext;
    return {
      VERTICES: 1,
      VERTEX_SHADER_SOURCE,
      FRAGMENT_SHADER_SOURCE,
      METHOD: POINTS,
      UNIFORMS,
      ATTRIBUTES: [
        { name: 'a_position', size: 2, type: FLOAT },
        { name: 'a_size', size: 1, type: FLOAT },
        { name: 'a_coreSize', size: 1, type: FLOAT },
        { name: 'a_color', size: 4, type: UNSIGNED_BYTE, normalized: true },
        { name: 'a_id', size: 4, type: UNSIGNED_BYTE, normalized: true },
      ],
    };
  }

  processVisibleItem(nodeIndex: number, startIndex: number, data: NodeDisplayData): void {
    const haloSize = data.size + Math.min(Math.max(data.size * HALO_FACTOR, HALO_MIN), HALO_MAX);
    const array = this.array;
    array[startIndex++] = data.x;
    array[startIndex++] = data.y;
    array[startIndex++] = haloSize;
    array[startIndex++] = data.size;
    array[startIndex++] = floatColor(data.color);
    array[startIndex++] = nodeIndex;
  }

  setUniforms(params: RenderParams, { gl, uniformLocations }: ProgramInfo): void {
    const { u_sizeRatio, u_pixelRatio, u_matrix } = uniformLocations;
    gl.uniform1f(u_pixelRatio, params.pixelRatio);
    gl.uniform1f(u_sizeRatio, params.sizeRatio);
    gl.uniformMatrix3fv(u_matrix, false, params.matrix);
  }
}

// Halo first (underneath), crisp circle on top
export const NodeGlowCircleProgram = createNodeCompoundProgram([NodeGlowProgram, NodeCircleProgram]);
